<template>
  <div>
    <div class="d-flex justify-content-end">
      <b-col cols="3">
        <multiselect v-model="template" :max-height="200"
                     placeholder="" label="name" track-by="name"
                     :options="rawDistributionGridTemplates" @input="applyTemplate"
                     :show-labels="false" :close-on-select="true"></multiselect>
      </b-col>
      <div class="d-flex justify-content-between button-group">
        <b-btn @click="saveTemplate" variant="secondary" size="sm">{{$t('buttons.common.save_template')}}</b-btn>
        <b-btn @click="removeTemplate" variant="danger" size="sm">{{$t('buttons.common.remove_template')}}</b-btn>
        <b-btn @click="updateTemplate" variant="success" size="sm">{{$t('buttons.common.update_template')}}</b-btn>
        <img src="@/assets/images/common-icons/icon_clear.png" @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg" @click="search"
             :title="$t('buttons.common.search')">
      </div>
    </div>
    <div class="d-flex justify-content-between toggle-container" @click="showCollapse = !showCollapse"
         :aria-expanded="showCollapse ? 'true' : 'false'"
         aria-controls="collapse">
      <h3>
        {{$t('labels.common.advanced_search')}}
      </h3>
      <div class="icons-wrapper">
        <icon name="angle-down" class="angle" v-if="showCollapse"></icon>
        <icon name="angle-right" class="angle" v-else></icon>
      </div>
    </div>
    <b-collapse id="collapse" v-model="showCollapse">
      <div class="search-form">
        <div class="search-form__filters">
          <b-row>
            <b-col>
              <label>{{$t('labels.production_planning.delivery_period')}}</label>
              <multiselect v-model="searchData.delivery_period" :max-height="200"
                           placeholder=""
                           :options="delivery_periods" :close-on-select="true"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.production_planning.group')}}</label>
              <multiselect v-model="searchData.groups" :max-height="200"
                           placeholder="" label="name" track-by="id"
                           :options="movement_groups" :multiple="true" :close-on-select="false"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.production_planning.order_status')}}
                <b-badge size="sm" variant="primary" id="orderStatusPopover">i</b-badge>
                <b-popover target="orderStatusPopover" triggers="hover focus">
                  <template slot="title">Order Status Information</template>
                  If there are <u>no selected values</u> in field <b>"Order status"</b>, raw distribution <u>will be
                  performed</u>
                  to products from <b>Final</b> and <b>Forecast</b> orders.
                </b-popover>
              </label>
              <multiselect v-model="searchData.sales_order_status" :max-height="200"
                           placeholder="" label="name" track-by="id"
                           :options="sales_order_statuses" :multiple="true" :close-on-select="false"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.thickness')}}</label>
              <multiselect v-model="searchData.thickness" :max-height="200"
                           placeholder="" :taggable="true" @tag="addTag($event, 'thickness')"
                           :options="[]" :multiple="true" :close-on-select="false"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.common.date')}}</label>
              <range-date-picker v-model="searchData.date" range lang="en" :type="datepickerType" placeholder=""
                                 :first-day-of-week="1" :disabled="!searchData.delivery_period"></range-date-picker>
            </b-col>
            <b-col>
              <label>{{$t('labels.purchase_orders.profile')}}</label>
              <multiselect v-model="searchData.profile" :max-height="200"
                           placeholder="" label="name" track-by="id"
                           :options="profiles" :multiple="true" :close-on-select="false"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.measure_type')}}</label>
              <multiselect v-model="searchData.measure_type" :max-height="200" label="name" track-by="id"
                           placeholder="" :options="filter_measure_types" @input="showHideColumns"
                           :multiple="true" :close-on-select="false"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.width')}}</label>
              <multiselect v-model="searchData.width" :max-height="200"
                           placeholder="" :taggable="true" @tag="addTag($event, 'width')"
                           :options="[]" :multiple="true" :close-on-select="false"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.common.wood')}}</label>
              <multiselect v-model="searchData.wood" :max-height="200"
                           placeholder="" label="name" track-by="id"
                           :options="wood" :multiple="true" :close-on-select="false"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.smart_search')}}
                <b-badge size="sm" variant="primary" id="smartSearchPopover">i</b-badge>
                <b-popover target="smartSearchPopover" triggers="hover focus">
                  <template slot="title">Smart Search Instruction</template>
                  Filter by package name. Packages names can differ for product groups (e.x. Bio Granules and
                  Obliuota)<br/>
                  Filter format: <strong>[T] [W] [L] [Profile] [Quality] [Impregnation] [Certificate] [Humidity]
                  [Package type] [Comments]</strong>, where <br/>
                  <strong>[T] [W] [L]</strong>
                  – product thickness, width and length; can be single value, set of values (e.x. 10,45) or range (e.x.
                  10-50)<br/>
                  <strong>[Profile] [Quality] [Impregnation] [Certificate]</strong>
                  – product profile, quality, impregnation and certificate; one or several symbols for each
                  parameter<br/>
                  <strong> [Humidity] </strong>
                  - product humidity; to find products by humidity parameter “%” is necessary <br/>
                  <strong>[Package type] [Comments]</strong>
                  - product package type and logo for granules; one or several symbols <br/>
                  Order of sizes is mandatory - thickness, width, length; order of other parameters is optional. All
                  matching is case insensitive.<br/>
                  <strong>Examples:</strong><br/>
                  <em>1. 45 95-110 2400 i-iii fsc 50%<br/>
                    2. 45-50 95,200 IV AB<br/>
                    3. pallet eco</em>
                </b-popover>
              </label>
              <input class="form-control" v-model="searchData.description" @keyup.enter="search">
            </b-col>
            <b-col>
              <label>{{$t('labels.common.virtual_stock')}}</label>
              <multiselect v-model="searchData.virtual_stock" :max-height="200"
                           placeholder="" label="text" track-by="text" :show-labels="false"
                           :options="includeOptions" :multiple="false"
                           :close-on-select="true"
              ></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.length')}}</label>
              <multiselect v-model="searchData.length" :max-height="200"
                           placeholder="" :taggable="true" @tag="addTag($event, 'length')"
                           :options="[]" :multiple="true" :close-on-select="false"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="3">
              <label>{{$t('labels.common.quality')}}</label>
              <multiselect v-model="searchData.quality" :max-height="200"
                           placeholder="" label="name" track-by="id"
                           :options="qualities_list" :multiple="true" :close-on-select="false"></multiselect>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-collapse>
    <div class="d-flex justify-content-end button-group">
      <img src="@/assets/images/common-icons/icon_xls.svg" @click="generateXLS"
           :title="$t('buttons.common.export')">
    </div>
    <ag-grid-vue class="ag-theme-balham" :gridOptions="gridOptions" :style="gridHeight"
                 :columnDefs="columns" :rowData="tableData" :pinnedBottomRowData="pinnedRowData"
                 :filterChanged="generatePinnedRowData" :rowDataChanged="generatePinnedRowData"
                 :postProcessPopup="postProcessPopup">
    </ag-grid-vue>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import {
    mapGetters,
    mapState
  } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import RangeDatePicker from 'vue2-datepicker';
  import moment from 'moment';
  import {
    cleanFilters,
    getArrayByProperty,
    setNotifyData,
    transformDateFromDateRangeToStringFormatByIndex
  } from '@/helpers/commonHelpers';
  import { gridMixins } from '@/mixins';

  export default {
    mixins: [gridMixins],
    components: {
      Multiselect,
      RangeDatePicker,
      'ag-grid-vue': AgGridVue
    },
    data() {
      return {
        template: '',
        showCollapse: true,
        gridName: 'raw_distribution',
        sales_order_statuses: [{name: 'Forecast', id: 'forecast'}, {name: 'Final', id: 'final'}],
        searchData: {
          delivery_period: '',
          groups: [],
          quality: [],
          thickness: [],
          date: '',
          profile: [],
          measure_type: [],
          width: [],
          wood: [],
          description: '',
          virtual_stock: null,
          length: [],
          sales_order_status: []
        },
        defaultGridTemplate: {
          params: [],
          filters: {}
        },
        pinnedRowData: []
      }
    },
    methods: {
      setDefaultData() {
        this.searchData.virtual_stock = this.includeOptions.find(item => item.text === 'Besides');
      },
      search() {
        const searchData = {
          thicknesses: this.searchData.thickness.toString(),
          widths: this.searchData.width.toString(),
          lengths: this.searchData.length.toString(),
          virtual_stock: this.searchData.virtual_stock && this.searchData.virtual_stock.key,
          smart_search: this.searchData.description,
          profiles: getArrayByProperty(this.searchData.profile, 'id').toString(),
          wood: getArrayByProperty(this.searchData.wood, 'id').toString(),
          groups: getArrayByProperty(this.searchData.groups, 'id').toString(),
          qualities: getArrayByProperty(this.searchData.quality, 'id').toString(),
          sales_order_status: getArrayByProperty(this.searchData.sales_order_status, 'id').toString(),
          date_start: transformDateFromDateRangeToStringFormatByIndex(this.searchData.date, 0),
          date_end: transformDateFromDateRangeToStringFormatByIndex(this.searchData.date, 1)
        }
        this.$store.dispatch('rawDistribution/loadDistributionTable', searchData);
      },
      cleanFilters() {
        cleanFilters(this.searchData);
        this.setDefaultData();
      },
      applyTemplate(e) {
        if (e) {
          this.gridOptions.columnApi.setColumnState(e.params);
          this.gridOptions.api.setFilterModel(e.filters);
        } else {
          this.searchData.measure_type = [];
          this.gridOptions.columnApi.setColumnState(this.defaultGridTemplate.params);
          this.gridOptions.api.setFilterModel(this.defaultGridTemplate.filters);
        }
      },
      saveTemplate() {
        this.$swal.fire({
          title: 'Creating grid template',
          input: 'text',
          inputPlaceholder: 'Enter grid template name',
          showCancelButton: true
        }).then(result => {
          const templateName = result.value;
          if (templateName) {
            const params = this.gridOptions.columnApi.getColumnState(),
              name = templateName, filters = this.gridOptions.api.getFilterModel(),
              grid_name = this.gridName;
            const savedData = {params, name, grid_name, filters};
            this.$http.post(`generic/pivot_grid_table_templates/`, savedData).then(async () => {
              await this.$store.dispatch('generic/loadGridTemplates', this.gridName);
              this.template = this.rawDistributionGridTemplates.find(e => e.name === templateName)
            });
          }
        });
      },
      removeTemplate() {
        if (this.template) {
          const templateId = this.template.id, templateName = this.template.name;
          this.$http.delete(`generic/pivot_grid_table_templates/${templateId}/`).then(async () => {
            await this.$store.dispatch('generic/loadGridTemplates', this.gridName);
            this.template = null;
            this.$notify(setNotifyData(`Grid template with name ${templateName} has been deleted`, '', 'success'));
            this.searchData.measure_type = [];
            this.gridOptions.columnApi.setColumnState(this.defaultGridTemplate.params);
            this.gridOptions.api.setFilterModel(this.defaultGridTemplate.filters);
          });
        }
      },
      updateTemplate() {
        if (this.template) {
          const templateId = this.template.id, templateName = this.template.name,
            params = this.gridOptions.columnApi.getColumnState(),
            grid_name = this.gridName, filters = this.gridOptions.api.getFilterModel();
          const savedData = {grid_name, filters, params};
          this.$http.put(`generic/pivot_grid_table_templates/${templateId}/`, savedData).then(() => {
            this.$notify(setNotifyData(`Grid template with name ${templateName} has been updated`, '', 'success'));
            this.$store.dispatch('generic/loadGridTemplates', this.gridName);
          });
        }
      },
      storeDefaultGridTemplate() {
        this.defaultGridTemplate.params = this.gridOptions.columnApi.getColumnState();
        this.defaultGridTemplate.filters = this.gridOptions.api.getFilterModel();
      },
      addTag(tag, field) {
        this.searchData[field].push(tag)
      },
      generateXLS() {
        const state = this.gridOptions.columnApi.getColumnState();

        const groupping_level = state.filter(item => {
          return item.rowGroupIndex !== null;
        }).length - 1;

        const paramsXLS = {
          allColumns: false,
          shouldRowBeSkipped: function (params) {
            return !params.node.group && params.node.level === 0 ? false :
              params.node.rowPinned && params.node.rowPinned === 'bottom' ? false :
                params.node.level !== groupping_level;
          },
          processCellCallback: function ({value}) {
            return (value && typeof value === 'string' && value.includes('->')) ? value.split('->')[1] : value;
          }
        };
        this.gridOptions.api.exportDataAsExcel(paramsXLS);
      },
      showHideColumns(measures) {
        this.columns.forEach(column => measures.find(type => column.field.includes(`_${type.id}`)) ?
          this.gridOptions.columnApi.setColumnVisible(column.field, true) :
          this.alwaysVisibleColumns.find(visible_column => visible_column === column.field) ? true :
            this.gridOptions.columnApi.setColumnVisible(column.field, false))
      },
      clearData() {
        this.$store.commit('rawDistribution/setDataForDistTable', []);
      },
      generatePinnedRowData() {
        this.pinnedRowData = [{...this.pinnedRowInitialData}];
        this.gridOptions.api.forEachNodeAfterFilter(node => Object.keys(this.pinnedRowData[0]).forEach(key => {
            if (this.pinnedRowData[0].sumOnlyNegativeValues.includes(key)) {
              if (node.data && node.data[key] < 0) {
                this.pinnedRowData[0][key] += node.data[key];
              }
            } else if (node.data) {
              this.pinnedRowData[0][key] += node.data[key];
            }
          }
        ));
        Object.keys(this.pinnedRowData[0]).forEach(key => {
          if (key.includes('_m3')) {
            this.pinnedRowData[0][key] = this.pinnedRowData[0][key].toFixed(3);
          } else if (key.includes('_m2') || key.includes('_lm')) {
            this.pinnedRowData[0][key] = this.pinnedRowData[0][key].toFixed(2);
          } else if (key.includes('_pcs')) {
            this.pinnedRowData[0][key] = this.pinnedRowData[0][key].toFixed();
          }
        })
      }
    },
    beforeMount() {
      this.$store.cache.dispatch('woodware/getWood');
      this.$store.cache.dispatch('woodware/getQualitiesList');
      this.$store.cache.dispatch('woodware/getProfiles');
      this.$store.dispatch('woodware/loadMovementGroups');
      this.$store.dispatch('generic/loadGridTemplates', this.gridName);
    },
    mounted() {
      this.setDefaultData();
      this.storeDefaultGridTemplate();
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        vm.clearData();
      });
    },
    computed: {
      ...mapState('rawDistribution', ['delivery_periods', 'columns', 'alwaysVisibleColumns', 'tableData',
        'pinnedRowInitialData', 'filter_measure_types']),
      ...mapState('generic', ['rawDistributionGridTemplates', 'includeOptions']),
      ...mapState('woodware', ['movement_groups', 'qualities_list', 'profiles', 'wood']),
      ...mapGetters('rawDistribution', ['gridOptions']),
      gridHeight() {
        if (window.innerHeight > 1024) {
          return this.showCollapse ? {height: 'calc(100vh - 460px)'} : {height: 'calc(100vh - 200px)'};
        } else {
          return this.showCollapse ? {height: '400px'} : {height: 'calc(100vh - 200px)'};
        }
      },
      datepickerType() {
        return this.searchData.delivery_period || 'date';
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/pages/modules/production-planning/raw-distribution/RawDistribution.vue